/**
 * Settings: devices, audio, push to talk, background, appearance, language,
 * translation, presence, account.
 *
 * One card per group, each a heading and its controls. No helper text under a
 * labelled control — the design system's content rules are explicit that it
 * restates what the label already says.
 *
 * ── The device lists come from the session, not from the browser ────────────
 * `CallSession.devices()` is the one place `enumerateDevices` is allowed to be
 * called from, and it answers with nameless entries until a call has held a
 * microphone — so an empty list here is the ordinary state before the first
 * call, not a failure. Settings says so once and offers the system default.
 *
 * ── The choices are this browser's, and they are kept here ──────────────────
 * A microphone id, a push-to-talk key and a background are per-device
 * preferences with nothing on the server behind them, so they live in
 * `localStorage` and are handed to the session when there is one. Storage can
 * throw — private mode, blocked site data — and every read and write is
 * wrapped for that reason; a Settings page that cannot save still works.
 *
 * Translation is the exception, and deliberately: it describes the reader, not
 * the machine, so it is the account's and lives on the server — the same
 * choice on every device. `lib/relay/translate.ts` holds it.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ds } from '../ds';
import { useI18n, useT } from '../i18n';
import { LANGUAGES, type Language } from '../i18n/languages';
import { savedTheme, setTheme, type ThemeChoice } from '../theme';
import { accountUrl } from '../lib/api';
import { store, useStore } from '../lib/relay/store';
import {
  translations, useTranslationLanguages, useTranslationState, type TranslationMode
} from '../lib/relay/translate';
import { openCallSession } from '../lib/calls/session';
import { readPrefs, writePrefs, type CallPrefs } from '../lib/calls/prefs';
import { Avatar } from '../components/Avatar';
import { PanelHeader } from '../components/Shell';
import type { Background, DeviceList } from '../lib/calls/types';
import type { Presence } from '../lib/relay/types';

const THEMES: ThemeChoice[] = ['light', 'dark', 'system'];
const PRESENCES: Presence[] = ['online', 'away', 'busy', 'offline'];

/**
 * One settings row: the label on the left, the toggle on the right.
 *
 * The bundle's `Switch` draws the toggle first and the label after it, which is
 * the design system's own shape and not a settings dialog's — there every row's
 * control sits on one right-hand edge and the labels read down the left
 * (`relay/src/ui/widgets.h`, `SwitchRow`). `row-reverse` is the whole of the
 * difference, and it is the whole of it on purpose: re-drawing the toggle to
 * move it would fork a component that has been signed off.
 *
 * `hint` is that class's optional second line. Nothing here passes one, and the
 * content rules are why — a hint that restates its label is the shape this
 * codebase keeps having to take back out.
 */
function SwitchRow({
  label,
  hint,
  checked,
  onChange
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  const { Switch } = ds();
  return (
    <Switch
      label={label}
      {...(hint ? { description: hint } : {})}
      checked={checked}
      onChange={onChange}
      style={{
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--space-8) 0'
      }}
    />
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  const { Card } = ds();
  return (
    <Card padding="panel" style={{ marginBottom: 'var(--space-16)' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono), monospace',
          fontSize: 11,
          letterSpacing: 'var(--ls-label)',
          textTransform: 'uppercase',
          color: 'var(--text-faint)',
          marginBottom: 'var(--space-12)'
        }}
      >
        {title}
      </div>
      {children}
    </Card>
  );
}

export function SettingsPage() {
  const { Button, Input, Radio, Select } = ds();
  const t = useT();
  const { language, choose } = useI18n();
  const me = useStore((s) => s.me());
  const translation = useTranslationState();
  const translateInto = useTranslationLanguages();

  const [theme, setChoice] = useState<ThemeChoice>(() => savedTheme());
  const [presence, setPresenceChoice] = useState<Presence>(() => me?.presence ?? 'online');
  const [prefs, setPrefs] = useState<CallPrefs>(readPrefs);
  const [devices, setDevices] = useState<DeviceList>({ mics: [], cams: [], speakers: [] });
  const [listening, setListening] = useState(false);

  // A session with no room: it is the device enumeration this page wants, and
  // asking for it is all it does. Nothing joins.
  const session = useMemo(() => openCallSession({ kind: 'call', id: '' }), []);

  useEffect(() => {
    let cancelled = false;
    void session.devices().then((list) => {
      if (!cancelled) setDevices(list);
    });
    return () => {
      cancelled = true;
    };
  }, [session]);

  const save = (patch: Partial<CallPrefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    writePrefs(next);
    return next;
  };

  // The next key pressed becomes the push-to-talk key.
  useEffect(() => {
    if (!listening) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      setListening(false);
      save({ pushToTalkKey: e.key === 'Escape' ? '' : e.key });
    };
    window.addEventListener('keydown', onKey, { once: true });
    return () => window.removeEventListener('keydown', onKey);
  });

  // A device nobody has granted a permission for answers with an empty
  // `deviceId` as well as an empty label, and so does the system-default row
  // above it. The Select keys its options by value, so those collide — and an
  // empty id already means "whatever the system picks", which is the row they
  // would be a second copy of.
  const options = (list: MediaDeviceInfo[]) => [
    { value: '', label: t('settings.devices.default') },
    ...list.filter((d) => d.deviceId).map((d) => ({ value: d.deviceId, label: d.label }))
  ];

  const nameless = devices.mics.length + devices.cams.length + devices.speakers.length === 0;

  const audio = (patch: Partial<CallPrefs>) => {
    const next = save(patch);
    void session.setAudioProcessing?.({
      noiseSuppression: next.noiseSuppression,
      echoCancellation: next.echoCancellation,
      autoGainControl: next.autoGainControl
    });
  };

  const background = (next: Background) => {
    save({ background: next });
    void session.setBackground(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PanelHeader title={t('settings.title')} />
      <div className="mv-scroll mv-page" style={{ flex: 1, padding: 'var(--space-20)' }}>
        {/* The measure is on the content, not on the scroller — capping the
            scroller puts its scrollbar in the middle of the panel. Centred,
            because a pane is as wide as the window and a column of cards held
            against its left edge reads as a page that failed to load. */}
        <div style={{ maxWidth: 'var(--container-prose, 620px)', margin: '0 auto' }}>
          <Group title={t('settings.devices')}>
            <div style={{ display: 'grid', gap: 'var(--space-12)' }}>
              <Select
                label={t('settings.devices.mic')}
                value={prefs.micId}
                options={options(devices.mics)}
                onChange={(e) => {
                  save({ micId: e.target.value });
                  void session.setMicrophone(e.target.value);
                }}
              />
              <Select
                label={t('settings.devices.camera')}
                value={prefs.camId}
                options={options(devices.cams)}
                onChange={(e) => save({ camId: e.target.value })}
              />
              <Select
                label={t('settings.devices.speaker')}
                value={prefs.speakerId}
                options={options(devices.speakers)}
                onChange={(e) => {
                  save({ speakerId: e.target.value });
                  void session.setSpeaker(e.target.value);
                }}
              />
              {nameless && (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  {t('settings.devices.none')}
                </div>
              )}
            </div>
          </Group>

          <Group title={t('settings.audio')}>
            <SwitchRow
              label={t('settings.audio.noise')}
              checked={prefs.noiseSuppression}
              onChange={(next) => audio({ noiseSuppression: next })}
            />
            <SwitchRow
              label={t('settings.audio.echo')}
              checked={prefs.echoCancellation}
              onChange={(next) => audio({ echoCancellation: next })}
            />
            <SwitchRow
              label={t('settings.audio.gain')}
              checked={prefs.autoGainControl}
              onChange={(next) => audio({ autoGainControl: next })}
            />
          </Group>

          <Group title={t('settings.pushtotalk')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-12)' }}>
              <span style={{ flex: 1, fontFamily: 'var(--font-mono), monospace' }}>
                {listening
                  ? t('settings.pushtotalk.press')
                  : prefs.pushToTalkKey || t('settings.pushtotalk.none')}
              </span>
              <Button size="sm" variant="secondary" onClick={() => setListening(true)}>
                {t('settings.pushtotalk.set')}
              </Button>
              {prefs.pushToTalkKey && (
                <Button size="sm" variant="ghost" onClick={() => save({ pushToTalkKey: '' })}>
                  {t('settings.pushtotalk.clear')}
                </Button>
              )}
            </div>
          </Group>

          <Group title={t('settings.background')}>
            <Radio
              value={prefs.background.kind}
              options={[
                { value: 'none', label: t('settings.background.none') },
                { value: 'blur', label: t('settings.background.blur') },
                { value: 'image', label: t('settings.background.image') }
              ]}
              onChange={(value: string) =>
                background(
                  value === 'image'
                    ? { kind: 'image', url: prefs.background.url ?? '' }
                    : { kind: value === 'blur' ? 'blur' : 'none' }
                )
              }
            />
            {prefs.background.kind === 'image' && (
              <Input
                label={t('settings.background.url')}
                value={prefs.background.url ?? ''}
                style={{ marginTop: 'var(--space-12)' }}
                onChange={(e) => background({ kind: 'image', url: e.target.value })}
              />
            )}
          </Group>

          <Group title={t('settings.appearance')}>
            <Select
              label={t('settings.theme.label')}
              value={theme}
              options={THEMES.map((c) => ({ value: c, label: t(`settings.theme.${c}`) }))}
              onChange={(e) => {
                const next = e.target.value as ThemeChoice;
                setChoice(next);
                setTheme(next);
              }}
            />
          </Group>

          <Group title={t('settings.language')}>
            <Select
              label={t('settings.language')}
              value={language}
              options={LANGUAGES.map((l) => ({ value: l.tag, label: l.label }))}
              onChange={(e) => choose(e.target.value as Language)}
            />
          </Group>

          {/* The server's answer says whether this deployment can translate at
              all, so the group is absent rather than greyed where it cannot. */}
          {translation?.available && (
            <Group title={t('settings.translation')}>
              <div style={{ display: 'grid', gap: 'var(--space-12)' }}>
                <Select
                  label={t('settings.translation.mode')}
                  value={translation.mode}
                  options={[
                    { value: 'auto', label: t('settings.translation.auto') },
                    { value: 'demand', label: t('settings.translation.demand') }
                  ]}
                  onChange={(e) => translations.save({ mode: e.target.value as TranslationMode })}
                />
                <Select
                  label={t('settings.translation.language')}
                  value={translation.language ?? ''}
                  options={[
                    { value: '', label: t('settings.translation.follow') },
                    ...translateInto.map((l) => ({ value: l.code, label: l.name }))
                  ]}
                  onChange={(e) =>
                    translations.save({ language: e.target.value === '' ? null : e.target.value })
                  }
                />
              </div>
            </Group>
          )}

          <Group title={t('settings.presence')}>
            <Select
              label={t('settings.presence')}
              value={presence}
              options={PRESENCES.map((p) => ({ value: p, label: t(`settings.presence.${p}`) }))}
              onChange={(e) => {
                const next = e.target.value as Presence;
                setPresenceChoice(next);
                store.setPresence(next);
              }}
            />
          </Group>

          <Group title={t('settings.account')}>
            {me && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-12)',
                  marginBottom: 'var(--space-16)'
                }}
              >
                <Avatar person={me} size={44} />
                <div>
                  <div style={{ fontWeight: 600 }}>{me.name}</div>
                  {me.handle && (
                    <div
                      style={{
                        color: 'var(--text-faint)',
                        fontFamily: 'var(--font-mono), monospace',
                        fontSize: 12
                      }}
                    >
                      @{me.handle}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Signing out is a POST to the accounts service, which owns the
                cookie and is on another origin — so it cannot be done from
                here, and a button that pretended to would leave somebody
                signed in. The link goes to the page that can. */}
            <Button variant="secondary" href={`${accountUrl()}/account`}>
              {t('settings.account.open')}
            </Button>
          </Group>
        </div>
      </div>
    </div>
  );
}
